import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FocusHeader } from '../components/FocusHeader';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { StepIndicator } from '../components/StepIndicator';
import { useSession } from '../contexts/SessionContext';
import { useAIGenerationEnabled } from '../hooks/useAIGenerationEnabled';
import { supabase } from '../lib/supabase';
import { getWizardSteps, useWizardNavigation } from '../lib/wizardSteps';
import { WIZARD_PATHS, wizardUrl } from '../lib/wizardRoutes';
import { CheckCircle, ArrowLeft } from 'lucide-react';

export function IdeaCustom() {
  const navigate = useNavigate();
  const location = useLocation();
  const { session, user, updateSession, refreshSession } = useSession();
  const { isEnabled: aiGenerationEnabled, loading: aiCheckLoading } = useAIGenerationEnabled();
  const [customIdea, setCustomIdea] = useState('');
  const [loading, setLoading] = useState(false);
  const [dataLoading, setDataLoading] = useState(true);
  const [dashboardSession, setDashboardSession] = useState<any>(null);
  const [dashboardUser, setDashboardUser] = useState<any>(null);

  useEffect(() => {
    if (!aiCheckLoading && !aiGenerationEnabled) {
      alert('AI generatie is tijdelijk uitgeschakeld');
      navigate('/dashboard');
      return;
    }

    const sessionId = location.state?.sessionId;

    if (sessionId) {
      loadDashboardData(sessionId);
    } else if (session && user) {
      setDataLoading(false);
    } else {
      setDataLoading(false);
    }
  }, [location.state?.sessionId, session, user, aiGenerationEnabled, aiCheckLoading, navigate]);

  const loadDashboardData = async (sessionId: string) => {
    setDataLoading(true);

    try {
      console.log('[IdeaCustom] Loading session by ID:', sessionId);

      // Load session
      const { data: sessionData, error: sessionError } = await supabase
        .from('sessions')
        .select('*')
        .eq('id', sessionId)
        .maybeSingle();

      if (sessionError || !sessionData) {
        console.error('[IdeaCustom] Error loading session:', sessionError);
        setDataLoading(false);
        return;
      }

      setDashboardSession(sessionData);

      // Load user
      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('*')
        .eq('id', sessionData.user_id)
        .maybeSingle();

      if (userError || !userData) {
        console.error('[IdeaCustom] Error loading user:', userError);
        setDataLoading(false);
        return;
      }

      console.log('[IdeaCustom] Session and user loaded');
      setDashboardUser(userData);
      setDataLoading(false);
    } catch (error) {
      console.error('[IdeaCustom] Exception loading dashboard data:', error);
      setDataLoading(false);
    }
  };

  const activeSession = dashboardSession || session;
  const { handleStepClick } = useWizardNavigation(activeSession);
  const activeUser = dashboardUser || user;

  if (loading || dataLoading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <FocusHeader
          title="Eigen idee"
          onBack={() => navigate('/dashboard')}
        />
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <LoadingSpinner text={loading ? "We maken een nieuw idee voor je..." : "Laden..."} />
        </main>
      </div>
    );
  }

  if (!activeSession || !activeUser) {
    return (
      <div className="min-h-screen bg-gray-50">
        <FocusHeader
          title="Eigen idee"
          onBack={() => navigate('/dashboard')}
        />
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="card bg-red-50 border-red-200">
            <h2 className="text-xl font-bold text-red-800 mb-2">Geen sessie gevonden</h2>
            <p className="text-red-700 mb-4">Er is geen actieve sessie beschikbaar.</p>
            <button
              onClick={() => navigate('/dashboard')}
              className="btn-secondary"
            >
              Terug naar Dashboard
            </button>
          </div>
        </main>
      </div>
    );
  }

  const handleApprove = async () => {
    if (!customIdea.trim()) {
      alert('Voer eerst je idee in.');
      return;
    }

    try {
      // Create new custom blog idea
      const { data: newIdea } = await supabase
        .from('blog_ideas')
        .insert({
          session_id: activeSession.id,
          user_id: activeUser.id,
          source: 'user_custom',
          idea_title: customIdea.substring(0, 100),
          idea_content: customIdea,
          idea_reason: 'Eigen idee van gebruiker',
          status: 'approved',
          decided_at: new Date().toISOString(),
        })
        .select()
        .single();

      if (newIdea) {
        // Update session
        await supabase
          .from('sessions')
          .update({
            state: 'idea_approved',
            approved_idea_id: newIdea.id,
          })
          .eq('id', activeSession.id);

        console.log('Session updated with custom idea, refreshing session context...');

        // Refresh session to load custom idea into context (if available)
        if (updateSession && refreshSession) {
          await updateSession({
            state: 'idea_approved',
            approved_idea_id: newIdea.id,
          });
          await refreshSession();
        }

        navigate(wizardUrl(WIZARD_PATHS.direction, activeSession.id));
      }
    } catch (error) {
      console.error('Error creating custom idea:', error);
      alert('Er ging iets mis. Probeer het opnieuw.');
    }
  };

  const handleBackToAI = async () => {
    setLoading(true);

    try {
      // Save current custom idea as rejected
      await supabase.from('blog_ideas').insert({
        session_id: activeSession.id,
        user_id: activeUser.id,
        source: 'user_custom',
        idea_title: customIdea.substring(0, 100) || 'Onvoltooid eigen idee',
        idea_content: customIdea || 'Gebruiker wilde toch AI',
        status: 'rejected',
        rejection_reason: 'Gebruiker wilde toch AI laten genereren',
        decided_at: new Date().toISOString(),
      });

      const { data: { session } } = await supabase.auth.getSession();
      const authToken = session?.access_token || (import.meta.env.VITE_SUPABASE_ANON_KEY);

      // Call OpenAI to generate new idea
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/openai-generate`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${authToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            type: 'idea',
            session_id: activeSession.id,
            user_id: activeUser.id,
            data: {},
          }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to generate idea');
      }

      let ideaData;
      if (data.success && data.data) {
        if (typeof data.data === 'string') {
          ideaData = JSON.parse(data.data);
        } else {
          ideaData = data.data;
        }
      } else if (data.result) {
        ideaData = data.result;
      } else {
        throw new Error('Onverwachte response structuur van AI');
      }

      // Create new blog idea
      await supabase.from('blog_ideas').insert({
        session_id: activeSession.id,
        user_id: activeUser.id,
        source: 'ai_regenerated',
        idea_title: ideaData.title,
        idea_content: ideaData.content,
        idea_reason: ideaData.reason || '',
        status: 'presented',
      });

      console.log('New idea created, updating session state to idea_selection...');

      // Update session state back to idea_selection so the new idea is shown
      await supabase
        .from('sessions')
        .update({
          state: 'idea_selection',
          approved_idea_id: null
        })
        .eq('id', activeSession.id);

      console.log('Session state updated, refreshing session context...');

      // Refresh session to load new idea (if available)
      if (refreshSession) {
        await refreshSession();
      }

      // Wait a bit longer to ensure state is fully updated
      await new Promise(resolve => setTimeout(resolve, 300));

      console.log('Navigating to idea check with new idea...');

      // Navigate back to idea check with sessionId
      navigate('/idea-check', {
        state: { sessionId: activeSession.id }
      });
    } catch (error) {
      console.error('Error generating new idea:', error);
      alert('Er ging iets mis bij het genereren van een nieuw idee. Probeer het opnieuw.');
    } finally {
      setLoading(false);
    }
  };

  const steps = getWizardSteps(activeSession, 1);

  return (
    <div className="min-h-screen bg-gray-50">
      <FocusHeader
        title="Eigen idee"
        onBack={() => navigate('/dashboard')}
      />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <StepIndicator steps={steps} onStepClick={handleStepClick} />
        <div className="space-y-6 mt-6">
          <div className="space-y-2">
            <h1 className="text-2xl md:text-3xl font-bold text-[#231F20]">
              Wat is jouw idee?
            </h1>
            <p className="text-gray-600">Geef een korte beschrijving van je blogidee</p>
          </div>

          <textarea
            className="textarea-field min-h-[200px] text-lg"
            placeholder="Bijvoorbeeld: Een artikel over hoe kleine bedrijven kunnen profiteren van AI-tools, met praktische voorbeelden..."
            value={customIdea}
            onChange={(e) => setCustomIdea(e.target.value)}
            autoFocus
          />

          <div className="space-y-3">
            <button
              onClick={handleApprove}
              disabled={!customIdea.trim()}
              className="btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <CheckCircle className="w-5 h-5" />
              Goedkeuren - Schrijf artikel
            </button>

            <button
              onClick={handleBackToAI}
              className="w-full text-gray-600 hover:text-[#231F20] py-3 flex items-center justify-center gap-2 font-medium transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Toch laten genereren door AI
            </button>
          </div>
        </div>
      </main>
    </div>
  );
}
